import * as z from 'zod'
import { useQuery } from '@tanstack/react-query'
import { PublicUser } from '~/app/api/users/public-user-schema'
import { isDev, isPreview } from '~/constants/routing'
import { useSelectedTeamAndRepo } from './use-selected-team-and-repo'
import { invariant } from '@epic-web/invariant'

/**
 * fetches user data from api
 * for "me", it'll get it fresh from useClerk.
 * if `null`, will use the current authenticated user
 */
export function useTeamAndRepoMembers() {
  const selectedTeamAndRepo = useSelectedTeamAndRepo()
  const teamId = selectedTeamAndRepo?.data?.team?.id
  const repoId = selectedTeamAndRepo?.data?.repo?.id

  const query = useQuery<{
    teamMembers: PublicUser[]
    repoGuests: PublicUser[]
  } | null>({
    queryKey: ['team-and-repo-members', teamId, repoId],
    queryFn: async () => {
      invariant(teamId, 'not enabled')
      invariant(repoId, 'not enabled')

      const res = await fetch(`/api/users?team-id=${teamId}&repo-id=${repoId}`)
      const data = await res.json()
      const parsedUsers = z
        .object({
          teamMembers: z.array(PublicUser),
          repoGuests: z.array(PublicUser),
        })
        .safeParse(data)
      if (!parsedUsers.success) {
        if (isPreview || isDev) return null
        throw new Error(`Invalid user data: ${parsedUsers.error.message}`)
      }
      if (!parsedUsers.data) {
        throw new Error(`No user data`)
      }

      return parsedUsers.data
    },
    enabled: !!teamId && !!repoId,
    staleTime: Infinity,
  })

  return query
}
